<template>
	<div id="app">
		<div v-if="loggedIn" class="app-layout">
			<div class="overlay" :class="{ show: showMobileSidebar }" @click="closeMobileSidebar"></div>
			<nav class="sidebar" :class="{ show: showMobileSidebar }">
				<div class="sidebar-header text-center">
					<b-navbar-brand to="/" class="app-logo">{{$title}}</b-navbar-brand>
				</div>
				<ul class="sidebar-menu nav flex-column">
					<li class="nav-item d-flex align-items-center" :class="{ 'nav-item-active': $route.name === 'Home' }">
						<router-link class="nav-link flex-fill" to="/" exact>Dashboard</router-link>
						<b-dropdown v-if="dash.active && dash.canEdit" right no-caret variant="link" size="sm" toggle-class="dashboard-nav-toggle" class="dashboard-nav-menu" title="Dashboard layout">
							<template #button-content><b-icon icon="three-dots-vertical"></b-icon></template>
							<b-dropdown-item v-if="dash.mode !== 'custom'" @click="dash.$emit('edit')"><b-icon icon="pencil-square" class="mr-1"></b-icon>Edit layout</b-dropdown-item>
							<template v-else>
								<b-dropdown-item-button v-for="p in dash.panels" :key="p.id" @click="dash.$emit('toggle-panel', p.id)">
									<b-icon :icon="p.visible ? 'check-square-fill' : 'square'" class="mr-2"></b-icon>{{ p.title }}
								</b-dropdown-item-button>
								<b-dropdown-divider></b-dropdown-divider>
								<b-dropdown-item @click="dash.$emit('reset')"><b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>Reset layout</b-dropdown-item>
							</template>
						</b-dropdown>
					</li>
					<li class="nav-item"><router-link class="nav-link" to="/widgets">Widgets</router-link></li>
					<li class="nav-item"><router-link class="nav-link" to="/songs">Songs <b-badge v-if="user && !user.is_subscribed" variant="warning" style="font-size: .65rem; vertical-align: top; position: relative; top: -2px">UPGRADE</b-badge></router-link></li>
					<li class="nav-item"><router-link class="nav-link" to="/commands">Commands</router-link></li>
					<li class="nav-item"><router-link class="nav-link" to="/timers">Timers</router-link></li>
					<li class="nav-item"><router-link class="nav-link" to="/urls">URLs</router-link></li>
					<li class="nav-item"><router-link class="nav-link" to="/chatters">Chatters</router-link></li>
					<li class="nav-item"><router-link class="nav-link" to="/settings">Settings</router-link></li>
				</ul>
				<div class="sidebar-footer text-center">
					<b-dropdown block dropup v-if="user?.is_admin" menu-class="w-100" text="Admin">
						<b-dropdown-item to="/admin">Admin</b-dropdown-item>
						<b-dropdown-item to="/admin/services">Services</b-dropdown-item>
						<b-dropdown-item to="/admin/channels">Channels</b-dropdown-item>
						<b-dropdown-item to="/admin/users">Users</b-dropdown-item>
						<b-dropdown-item to="/admin/voices">Voices</b-dropdown-item>
						<b-dropdown-item to="/admin/giftTypes">Gift types</b-dropdown-item>
						<b-dropdown-item to="/admin/categories">Categories</b-dropdown-item>
					</b-dropdown>

					<b-dropdown block dropup menu-class="w-100">
						<template #button-content>{{user?.username}} <b-badge :variant="user?.plan ? 'success' : 'secondary'">{{user?.is_subscribed&&user?.plan||'free'}}</b-badge></template>
						<b-dropdown-item to="/account" active-class="active">Account</b-dropdown-item>
						<b-dropdown-item to="/account/billing" active-class="active">Billing</b-dropdown-item>
						<b-dropdown-item v-if="isSubUser" @click="popOut">Return to previous user</b-dropdown-item>
						<b-dropdown-item @click="logOut">Sign out</b-dropdown-item>
					</b-dropdown>
				</div>
			</nav>
			<main class="main-content" :class="{ expanded: showMobileSidebar }">
				<div class="top-nav navbar-dark">
					<button class="hamburger-btn" @click="toggleMobileSidebar"><span class="navbar-toggler-icon"></span></button>
					<b-navbar-brand to="/" class="mobile-logo">{{$title}}</b-navbar-brand>
					<div style="width: 30px;"></div>
				</div>
				<b-container class="flex-fill d-flex flex-column" :class="{'overflow-hidden': $route.name == 'Home' || $route.name == 'Songs', 'py-2': $route.name != 'Songs' && !dashboardGrid, 'px-0': $route.name == 'Songs' || dashboardGrid }" :fluid="$route.name == 'Songs' || dashboardGrid">

					<div class="text-center" v-if="loading>0">
						<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
					</div>
					<b-alert variant="danger" show v-else-if="error">{{error}}</b-alert>
					<router-view v-else-if="user"/>
					<b-alert variant="danger" show v-else>No userdata</b-alert>
				<upgrade-modal ref="upgradeModal" :user="user"></upgrade-modal>
				</b-container>
			</main>
		</div>
		<b-container class="mt-3 mb-3" v-else>
			<b-row>
				<b-col cols="12" md="3" lg="4">
					<a href="/">&laquo; Back to front page</a>
				</b-col>
				<b-col cols="12" md="6" lg="4">
					<h1 class="text-center mb-5"><span style="color: #5df286; text-shadow: 0 0 20px #5df286">Live</span> <span style="color: #eb5dff; text-shadow: 0 0 20px #eb5dff">Joiner</span></h1>
					<div v-if="$route.name == 'Register' || $route.name == 'Confirm' || $route.name == 'Recover'">
						<h5 class="text-center">{{$route.name}}</h5>
						<router-view/>
					</div>
					<b-form @submit.stop.prevent="submitLogin" v-else>
						<h5 class="text-center">Login</h5>
						<b-form-group
							id="login-username-group"
							label="Username:"
							label-for="login-username"
						>
							<b-form-input
								id="login-username"
								v-model="loginForm.username"
								type="text"
								placeholder="Enter username"
								required
							></b-form-input>
						</b-form-group>
						<b-form-group id="login-password-group" label="Password:" label-for="login-password">
							<b-form-input
								id="login-password"
								type="password"
								v-model="loginForm.password"
								placeholder="Enter password"
								required
							></b-form-input>
						</b-form-group>
						<b-alert variant="danger" v-show="loginForm.message" show class="mb-2">{{loginForm.message}}</b-alert>
					<b-alert variant="warning" v-if="loginForm.emailNotConfirmed" show class="mb-2">
						Your email address has not been confirmed. Please check your inbox.
						<div class="mt-2">
							<b-spinner v-if="loginForm.resendLoading" small></b-spinner>
							<b-button v-else size="sm" variant="warning" @click="resendFromLogin">Resend confirmation email</b-button>
						</div>
						<div v-if="loginForm.resendMessage" class="mt-1 text-success small">{{loginForm.resendMessage}}</div>
						<div v-if="loginForm.resendError" class="mt-1 text-danger small">{{loginForm.resendError}}</div>
					</b-alert>
						<b-row class="mb-2">
							<b-col>
								<b-spinner v-show="loginForm.loading" small label="Loading"></b-spinner>
							</b-col>
							<b-col class="text-right">
								<b-button variant="success" type="submit" class="ml-3">Login</b-button>
							</b-col>
						</b-row>
						<b-row class="mb-2">
							<b-col>
								<router-link to="/register">Register</router-link>
							</b-col>
							<b-col class="text-right">
								<router-link to="/recover">Forgot password?</router-link>
							</b-col>
						</b-row>
					</b-form>
				</b-col>
			</b-row>
		</b-container>
	</div>
</template>

<script>
import UpgradeModal from '@/components/User/Modal/Upgrade.vue';
import dashboardControls from '@/dashboardControls';

export default {
	components: { UpgradeModal },
	data() { return {
		dash: dashboardControls,
		loading: 0,
		error: null,
		loggedIn: false,
		isSubUser: false,
		loginForm: {
			loading: false,
			username: '',
			password: '',
			message: null,
			emailNotConfirmed: false,
			emailNotConfirmedEmail: null,
			resendLoading: false,
			resendMessage: null,
			resendError: null,
		},
		user: null,
		showMobileSidebar: false,
		activeMenu: 'dashboard'
	}},
	watch: {
		$route() {
			this.showMobileSidebar = false;
			this.loginForm.loading = false;
			this.loginForm.username = '';
			this.loginForm.password = '';
			this.loginForm.message = '';
			this.loginForm.emailNotConfirmed = false;
			this.loginForm.emailNotConfirmedEmail = null;
			this.loginForm.resendMessage = null;
			this.loginForm.resendError = null;
		}
	},
	mounted() {
		this.$root.$on('show-upgrade-modal', () => this.$refs.upgradeModal?.show());
		this.$auth.addEventListener('authChanged', this.authChanged.bind(this))
		this.$auth.loadFromLocalStorage();
		// Handle window resize to close mobile sidebar if screen becomes wider
		window.addEventListener('resize', () => { if(window.innerWidth > 768) this.showMobileSidebar = false });
		this.initSwipeGesture();
	},
	beforeDestroy() {
		this.$root.$off('show-upgrade-modal');
		this.$auth.removeEventListener('authChanged', this.authChanged.bind(this))
		document.removeEventListener('touchstart', this._onTouchStart);
		document.removeEventListener('touchmove', this._onTouchMove);
		document.removeEventListener('touchend', this._onTouchEnd);
	},
	computed: {
		// The Dashboard only goes full-width in the editable grid mode; the default (classic)
		// layout stays restrained like the rest of the app (Widgets etc.).
		dashboardGrid() {
			return this.$route.name === 'Home' && this.dash.mode === 'custom' && this.dash.canEdit;
		},
	},
	methods: {
		authChanged(ev) {
			this.loggedIn = ev.detail.isLoggedIn;
			this.isSubUser = ev.detail.isSubUser;
			if(!this.loggedIn) {
				this.user = null;
				return;
			}

			this.reloadUserData();
		},
		async reloadUserData() {
			try {
				this.loading++;
				this.error = null;
				await this.loadUserData();
				if(this.$route.query.plan || this.$route.query.period) {
					await this.$nextTick();
					this.$refs.upgradeModal?.show({ plan: this.$route.query.plan || null, period: this.$route.query.period || null });
				}
			} catch(err) {
				this.error = err;
				this.user = null;
				console.error('Could not get user data', err);
			} finally {
				this.loading--;
			}
		},
		async loadUserData() {
			this.user = await this.$api.get('userData');
		},
		submitLogin: function() {
			this.loginForm.loading = true;
			this.loginForm.message = '';
			this.$api.post('auth', {
				username: this.loginForm.username,
				password: this.loginForm.password
			}).then(result => {
				const params = new Proxy(new URLSearchParams(window.location.search), {
					get: (searchParams, prop) => searchParams.get(prop),
				});
				if(params.redirect) {
					this.$auth.store(result.token);
					document.location = params.redirect;
					return;
				}
				this.$auth.load(result.token);
				this.$bvToast.toast('Logged in', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
				this.$bvModal.hide('login-modal');
				this.loginForm.username = '';
				this.loginForm.password = '';
			}).catch(err => {
				console.error(err);
				if(err.code === 'email_not_confirmed') {
					this.loginForm.emailNotConfirmed = true;
					this.loginForm.emailNotConfirmedEmail = err.email;
				} else {
					this.loginForm.message = err;
				}
			}).finally(() => {
				this.loginForm.loading = false;
			});

			return false;
		},
		loginOk(bvModalEvt) {
			bvModalEvt.preventDefault();
			this.submitLogin();
		},
		resetLogin: function() {
			this.loginForm.username = '';
			this.loginForm.password = '';
			this.loginForm.message = '';
			this.loginForm.emailNotConfirmed = false;
			this.loginForm.emailNotConfirmedEmail = null;
			this.loginForm.resendMessage = null;
			this.loginForm.resendError = null;
		},
		async resendFromLogin() {
			this.loginForm.resendLoading = true;
			this.loginForm.resendMessage = null;
			this.loginForm.resendError = null;
			try {
				const result = await this.$api.post('resendConfirmation', { email: this.loginForm.emailNotConfirmedEmail });
				const next = result.nextAllowedAt ? new Date(result.nextAllowedAt).toLocaleTimeString() : null;
				this.loginForm.resendMessage = next ? `Email sent! Next resend allowed at ${next}.` : 'Email sent!';
			} catch(err) {
				if(err.code === 'rate_limited') {
					const next = new Date(err.nextAllowedAt).toLocaleTimeString();
					this.loginForm.resendError = `Please wait until ${next} before requesting another email.`;
				} else if(err.code === 'too_many_attempts') {
					this.loginForm.resendError = 'Too many resend attempts. Please contact support.';
				} else {
					this.loginForm.resendError = err.message;
				}
			} finally {
				this.loginForm.resendLoading = false;
			}
		},
		logOut() {
			delete localStorage.username;
			delete localStorage.authToken;
			this.$auth.setToken(null);
		},
		popOut() {
			this.$auth.popToken();
			this.$router.push({ path: `/admin/users` });
		},
		toggleMobileSidebar() {
			this.showMobileSidebar = !this.showMobileSidebar;
		},
		closeMobileSidebar() {
			this.showMobileSidebar = false;
		},
		initSwipeGesture() {
			const SIDEBAR_WIDTH = 200;
			let startX = 0, startY = 0, startTime = 0, startOpen = false, swiping = false, locked = false;

			const getSidebar = () => this.$el?.querySelector('.sidebar');
			const getOverlay = () => this.$el?.querySelector('.overlay');

			const setSwipePosition = (translateX) => {
				const s = getSidebar(), o = getOverlay();
				if(!s || !o) return;
				const progress = (SIDEBAR_WIDTH + translateX) / SIDEBAR_WIDTH;
				s.style.transition = 'none';
				s.style.transform = `translateX(${translateX}px)`;
				o.style.transition = 'none';
				o.style.opacity = progress;
				o.style.visibility = progress > 0 ? 'visible' : 'hidden';
			};

			const clearSwipeStyles = () => {
				const s = getSidebar(), o = getOverlay();
				if(!s) return;
				s.style.transition = '';
				s.style.transform = '';
				if(!o) return;
				o.style.transition = '';
				o.style.opacity = '';
				o.style.visibility = '';
			};

			const getTranslateX = (dx) => {
				if(startOpen) return Math.max(-SIDEBAR_WIDTH, Math.min(0, dx));
				return Math.max(-SIDEBAR_WIDTH, Math.min(0, -SIDEBAR_WIDTH + dx));
			};

			this._onTouchStart = (e) => {
				if(window.innerWidth > 768) return;
				startX = e.touches[0].clientX;
				startY = e.touches[0].clientY;
				startTime = Date.now();
				startOpen = this.showMobileSidebar;
				swiping = false;
				locked = false;
			};

			this._onTouchMove = (e) => {
				if(window.innerWidth > 768) return;
				const dx = e.touches[0].clientX - startX;
				const dy = e.touches[0].clientY - startY;

				if(locked) return;
				if(!swiping) {
					if(Math.abs(dx) < 20 && Math.abs(dy) < 20) return;
					if(Math.abs(dy) > Math.abs(dx) * 0.5) { locked = true; return; }
					if(!startOpen && dx < 0) { locked = true; return; }
					if(startOpen && dx > 0) { locked = true; return; }
					swiping = true;
				}

				setSwipePosition(getTranslateX(dx));
			};

			this._onTouchEnd = (e) => {
				if(!swiping) return;
				swiping = false;
				const dx = e.changedTouches[0].clientX - startX;
				const elapsed = Date.now() - startTime;
				const velocity = Math.abs(dx) / elapsed; // px/ms
				const translateX = getTranslateX(dx);
				clearSwipeStyles();
				// Quick flick (>0.3 px/ms) commits in the swipe direction
				if(velocity > 0.3)
					this.showMobileSidebar = dx > 0;
				else
					this.showMobileSidebar = (SIDEBAR_WIDTH + translateX) > SIDEBAR_WIDTH / 3;
			};

			document.addEventListener('touchstart', this._onTouchStart, { passive: true });
			document.addEventListener('touchmove', this._onTouchMove, { passive: true });
			document.addEventListener('touchend', this._onTouchEnd, { passive: true });
		},
		setActiveMenu(menu) {
			this.activeMenu = menu;
			// Close mobile sidebar when menu item is clicked
			this.showMobileSidebar = false;

			// Update active state
			this.$nextTick(() => {
				document.querySelectorAll('.nav-link').forEach(link => {
					link.classList.remove('active');
				});
				event.target.classList.add('active');
			});
		}
	},
}
</script>

<style lang="scss">
@import './plugins/variable.scss';

/* Dashboard nav item: the whole row (link + ⋮) takes the active highlight, and the ⋮ toggle
   is transparent so it blends into that highlight instead of showing a mismatched dark rect. */
.sidebar-menu .nav-item-active {
	background-color: $gray-600;
}
.dashboard-nav-menu .btn.dashboard-nav-toggle,
.dashboard-nav-menu .btn.dashboard-nav-toggle:focus,
.dashboard-nav-menu .btn.dashboard-nav-toggle:active,
.dashboard-nav-menu.show .btn.dashboard-nav-toggle {
	color: white !important;
	background-color: transparent;
	background-image: none;
	border: none;
	box-shadow: none;
	width: 34px;
	height: 34px;
	padding: 0;
	border-radius: 5px;
	opacity: .85;
	transition: background-color .15s ease, opacity .15s ease;
}
.dashboard-nav-menu .btn.dashboard-nav-toggle:hover,
.dashboard-nav-menu.show .btn.dashboard-nav-toggle {
	opacity: 1;
	background-color: rgba(255, 255, 255, .15);
}

.app-layout {
	height: 100vh;
	overflow: hidden;

	> .sidebar {
		width: 200px;
		height: 100vh;
		position: fixed;
		left: 0;
		top: 0;
		background: $gray-200;
		transition: transform 0.3s ease;
		display: flex;
		flex-direction: column;
		z-index: 500;

		&.collapsed {
			transform: translateX(-100%);
		}

		> .sidebar-header {
			padding: 10px;
			border-bottom: 1px solid $border-color;
			flex-shrink: 0;

			> .app-logo {
				color: white;
				font-size: 1.5rem;
				text-decoration: none;
				margin-right: 0;
			}
		}
		> .sidebar-menu {
			flex: 1;
			overflow-y: auto;
			padding: 0;
			margin: 0;
			flex-wrap: nowrap;

			.nav-link {
				color: $blue !important;
			}

			.nav-link:hover,
			.nav-link.active,
			.nav-link.active-tree {
				background-color: $gray-600;
				color: white !important;
			}
		}

		> .sidebar-footer {
			text-align: center;
			border-top: 1px solid $border-color;
			flex-shrink: 0;
		}
	}
	.main-content {
		display:flex;
		flex-direction: column;
		margin-left: 200px;
		transition: margin-left 0.3s ease;
		height: 100vh;
		overflow-y: auto;
		&.expanded {
			margin-left: 0;
		}
	}

	.top-nav {
		background: $gray-200;
		padding: 10px 20px;
		display: none;
		align-items: center;
		justify-content: space-between;
	}

	.hamburger-btn {
		background: none;
		border: none;
		font-size: 1.2rem;
	}

	.mobile-logo {
	}

	.overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(3px);
		z-index: 400;
		opacity: 0;
		visibility: hidden;
		transition: all 0.3s ease;
	}

	.overlay.show {
		opacity: 1;
		visibility: visible;
	}

	@media (max-width: 768px) {
		.sidebar {
			transform: translateX(-100%);
			&.show {
				transform: translateX(0);
			}
		}

		.main-content {
			margin-left: 0;
		}

		.top-nav {
			display: flex;
		}
	}
}
</style>
